import { useState, useEffect, useRef } from 'react';
import { apiFetch } from '../utils/api';
import { 
  Package, Search, Plus, Edit2, Trash2, Eye, 
  AlertCircle, CheckCircle, X, Image, Tag, Layers,
  Upload, Link, Store
} from 'lucide-react';

interface Product {
  id: string;
  skuCode: string;
  janCode: string | null;
  productName: string;
  productNameKana?: string | null;
  description?: string | null;
  sellingPrice: string;
  costPrice: string | null;
  imageUrls: string[];
  status: string;
  variantCount: number;
  createdAt: string;
}

interface Variant {
  id: string;
  variantCode: string;
  size?: string;
  color?: string;
  janCode?: string;
  additionalPrice?: string;
  status: string;
  inventory?: {
    logicalStock: number;
    allocatedStock: number;
    freeStock: number;
    safetyStock: number;
  };
}

interface Shop {
  id: string;
  shopName: string;
  platformType: string;
}

export default function ProductsPage() {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [search, setSearch] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [viewingProduct, setViewingProduct] = useState<string | null>(null);

  useEffect(() => {
    fetchProducts();
  }, []);

  async function fetchProducts() {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        limit: '50',
        ...(search && { search }),
      });

      const response = await apiFetch(`/api/products?${params}`);
      const data = await response.json();

      if (!response.ok) throw new Error(data.error);
      setProducts(Array.isArray(data.products) ? data.products : []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'エラーが発生しました');
    } finally {
      setLoading(false);
    }
  }

  async function deleteProduct(id: string) {
    if (!confirm('この商品を削除しますか？')) return;

    try {
      const response = await apiFetch(`/api/products/${id}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error);
      }

      setSuccess('商品を削除しました');
      fetchProducts();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'エラーが発生しました');
    }
  }

  return (
    <div className="space-y-6">
      {/* ヘッダー */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
            <Package size={28} />
            商品管理
          </h1>
          <p className="mt-1" style={{ color: 'var(--text-secondary)' }}>
            商品マスタ、バリエーション、モール連携を管理
          </p>
        </div>
        <button onClick={() => setShowCreateModal(true)} className="btn-primary flex items-center gap-2">
          <Plus size={18} />
          商品登録
        </button>
      </div>

      {/* 検索 */}
      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
            <Search size={18} />
          </span>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && fetchProducts()}
            placeholder="商品名、SKUコード、JANコードで検索..."
            className="w-full pl-10 pr-4 py-2 border rounded-lg"
            style={{ backgroundColor: 'var(--background-card)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
          />
        </div>
        <button onClick={fetchProducts} className="btn-primary">検索</button>
      </div>

      {/* メッセージ */}
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
          <AlertCircle size={18} />
          <span>{error}</span>
          <button onClick={() => setError('')} className="ml-auto"><X size={16} /></button>
        </div>
      )}
      {success && (
        <div className="p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2 text-green-700">
          <CheckCircle size={18} />
          <span>{success}</span>
          <button onClick={() => setSuccess('')} className="ml-auto"><X size={16} /></button>
        </div>
      )}

      {/* 商品一覧 */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {loading ? (
          <div className="col-span-full p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4" style={{ color: 'var(--text-secondary)' }}>読み込み中...</p>
          </div>
        ) : products.length === 0 ? (
          <div className="col-span-full card p-8 text-center">
            <Package size={48} className="mx-auto mb-4 opacity-50" />
            <p style={{ color: 'var(--text-secondary)' }}>商品がありません</p>
            <button onClick={() => setShowCreateModal(true)} className="btn-primary mt-4">
              <Plus size={16} className="inline mr-1" />
              最初の商品を登録
            </button>
          </div>
        ) : (
          products.map((product) => (
            <div key={product.id} className="card overflow-hidden hover:shadow-lg transition-shadow">
              {/* 画像 */}
              <div className="aspect-video bg-gray-100 flex items-center justify-center relative group">
                {product.imageUrls && product.imageUrls.length > 0 ? (
                  <>
                    <img src={product.imageUrls[0]} alt={product.productName} className="w-full h-full object-cover" />
                    {product.imageUrls.length > 1 && (
                      <span className="absolute bottom-2 right-2 bg-black/60 text-white text-xs px-2 py-1 rounded">
                        +{product.imageUrls.length - 1}
                      </span>
                    )}
                  </>
                ) : (
                  <Image size={48} className="text-gray-300" />
                )}
              </div>
              
              {/* 情報 */}
              <div className="p-4">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold truncate" style={{ color: 'var(--text-primary)' }}>
                      {product.productName}
                    </h3>
                    <p className="text-sm font-mono" style={{ color: 'var(--text-secondary)' }}>
                      {product.skuCode}
                    </p>
                  </div>
                  <span className={`ml-2 px-2 py-1 text-xs rounded ${
                    product.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                  }`}>
                    {product.status === 'active' ? '有効' : '無効'}
                  </span>
                </div>

                <div className="flex items-center gap-4 text-sm mb-3" style={{ color: 'var(--text-secondary)' }}>
                  <span className="flex items-center gap-1">
                    <Tag size={14} />
                    ¥{Number(product.sellingPrice).toLocaleString()}
                  </span>
                  <span className="flex items-center gap-1">
                    <Layers size={14} />
                    {product.variantCount}バリエーション
                  </span>
                </div>

                {/* アクション */}
                <div className="flex items-center gap-2 pt-3 border-t" style={{ borderColor: 'var(--border-color)' }}>
                  <button
                    onClick={() => setViewingProduct(product.id)}
                    className="flex-1 py-2 text-sm rounded-lg hover:bg-gray-100 transition-colors flex items-center justify-center gap-1"
                    style={{ color: 'var(--text-primary)' }}
                  >
                    <Eye size={16} />
                    詳細
                  </button>
                  <button
                    onClick={() => setEditingProduct(product)}
                    className="flex-1 py-2 text-sm rounded-lg hover:bg-gray-100 transition-colors flex items-center justify-center gap-1"
                    style={{ color: 'var(--text-primary)' }}
                  >
                    <Edit2 size={16} />
                    編集
                  </button>
                  <button
                    onClick={() => deleteProduct(product.id)}
                    className="py-2 px-3 text-sm rounded-lg hover:bg-red-50 transition-colors text-red-600"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* モーダル */}
      {showCreateModal && (
        <CreateProductModal
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => {
            setShowCreateModal(false);
            setSuccess('商品を登録しました');
            fetchProducts();
          }}
        />
      )}

      {editingProduct && (
        <EditProductModal
          product={editingProduct}
          onClose={() => setEditingProduct(null)}
          onSuccess={() => {
            setEditingProduct(null);
            setSuccess('商品を更新しました');
            fetchProducts();
          }}
        />
      )}

      {viewingProduct && (
        <ProductDetailModal
          productId={viewingProduct}
          onClose={() => setViewingProduct(null)}
          onEdit={(product) => {
            setViewingProduct(null);
            setEditingProduct(product);
          }}
        />
      )}
    </div>
  );
}

// 画像アップロードコンポーネント
function ImageUploader({ images, onChange, productId }: { 
  images: string[]; 
  onChange: (images: string[]) => void;
  productId?: string;
}) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');

  async function handleFileSelect(e: React.ChangeEvent<HTMLInputElement>) {
    const files = e.target.files;
    if (!files || files.length === 0) return;

    setUploading(true);
    setError('');
    const newImages: string[] = [];

    for (const file of Array.from(files)) {
      if (!file.type.startsWith('image/')) {
        setError('画像ファイルのみアップロードできます');
        continue;
      }
      
      // 5MBチェック
      if (file.size > 5 * 1024 * 1024) {
        setError('5MB以下の画像を選択してください');
        continue;
      }
      
      // ファイルをBase64に変換
      const base64 = await new Promise<string>((resolve) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.readAsDataURL(file);
      });

      if (productId) {
        // 既存商品の場合はAPIでアップロード
        try {
          const response = await apiFetch(`/api/products/${productId}/images`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              images: [{
                data: base64,
                contentType: file.type,
              }],
            }),
          });
          const data = await response.json();
          if (response.ok && data.imageUrls) {
            onChange(data.imageUrls);
          } else {
            setError(data.error || '画像のアップロードに失敗しました');
          }
        } catch (err) {
          console.error('Upload error:', err);
          setError('画像のアップロードに失敗しました');
        }
      } else {
        // 新規商品の場合はプレビュー用にBase64を保持
        newImages.push(base64);
      }
    }

    if (!productId && newImages.length > 0) {
      onChange([...images, ...newImages].slice(0, 20));
    }

    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  async function removeImage(index: number) {
    const imageUrl = images[index];
    
    if (productId && !imageUrl.startsWith('data:')) {
      // APIで削除
      try {
        await apiFetch(`/api/products/${productId}/images`, {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ imageUrl }),
        });
      } catch (err) {
        console.error('Delete error:', err);
      }
    }
    
    const updated = images.filter((_, i) => i !== index);
    onChange(updated);
  }

  return (
    <div>
      <label className="block text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>
        商品画像 <span className="text-gray-400">（最大20枚・1枚5MBまで）</span>
      </label>
      {error && (
        <p className="text-red-600 text-sm mb-2">{error}</p>
      )}
      <div className="grid grid-cols-5 gap-2">
        {images.map((img, i) => (
          <div key={i} className="relative aspect-square bg-gray-100 rounded-lg overflow-hidden group">
            <img src={img} alt={`商品画像${i + 1}`} className="w-full h-full object-cover" />
            <button
              type="button"
              onClick={() => removeImage(i)}
              className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <X size={12} />
            </button>
            {i === 0 && (
              <span className="absolute bottom-1 left-1 bg-blue-600 text-white text-xs px-1 rounded">メイン</span>
            )}
          </div>
        ))}
        {images.length < 20 && (
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            className="aspect-square border-2 border-dashed rounded-lg flex flex-col items-center justify-center gap-1 hover:border-blue-400 hover:bg-blue-50 transition-colors"
            style={{ borderColor: 'var(--border-color)' }}
          >
            {uploading ? (
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
            ) : (
              <>
                <Upload size={20} className="text-gray-400" />
                <span className="text-xs text-gray-400">追加</span>
              </>
            )}
          </button>
        )}
      </div>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/gif,image/webp"
        multiple
        onChange={handleFileSelect}
        className="hidden"
      />
    </div>
  );
}

// 商品作成モーダル
function CreateProductModal({ onClose, onSuccess }: { onClose: () => void; onSuccess: () => void }) {
  const [formData, setFormData] = useState({
    skuCode: '',
    janCode: '',
    productName: '',
    productNameKana: '',
    description: '',
    costPrice: '',
    sellingPrice: '',
  });
  const [images, setImages] = useState<string[]>([]);
  const [variants, setVariants] = useState<Array<{ variantCode: string; size: string; color: string; janCode: string; initialStock: number }>>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  function addVariant() {
    setVariants([...variants, { variantCode: '', size: '', color: '', janCode: '', initialStock: 0 }]);
  }

  function updateVariant(index: number, field: string, value: any) {
    const updated = [...variants];
    (updated[index] as any)[field] = value;
    setVariants(updated);
  }

  function removeVariant(index: number) {
    setVariants(variants.filter((_, i) => i !== index));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const pendingImages = images.filter(img => img.startsWith('data:'));

      const response = await apiFetch('/api/products', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          productNameKana: formData.productNameKana || undefined,
          costPrice: formData.costPrice ? parseFloat(formData.costPrice) : undefined,
          sellingPrice: parseFloat(formData.sellingPrice),
          variants: variants.length > 0 ? variants : undefined,
        }),
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.error);

      // 保留中の画像をアップロード
      if (pendingImages.length > 0 && data.product?.id) {
        await apiFetch(`/api/products/${data.product.id}/images`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            images: pendingImages.map((img) => ({
              data: img,
              contentType: 'image/jpeg',
            })),
          }),
        });
      }

      onSuccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'エラーが発生しました');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-lg shadow-xl" style={{ backgroundColor: 'var(--background-card)' }}>
        <div className="p-6">
          <h2 className="text-xl font-bold mb-4" style={{ color: 'var(--text-primary)' }}>商品登録</h2>

          {error && <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">{error}</div>}

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* 画像アップロード */}
            <ImageUploader images={images} onChange={setImages} />

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-primary)' }}>SKUコード *</label>
                <input type="text" value={formData.skuCode} onChange={(e) => setFormData({ ...formData, skuCode: e.target.value })} required className="w-full px-4 py-2 border rounded-lg" style={{ backgroundColor: 'var(--background-card)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }} />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-primary)' }}>JANコード</label>
                <input type="text" value={formData.janCode} onChange={(e) => setFormData({ ...formData, janCode: e.target.value })} className="w-full px-4 py-2 border rounded-lg" style={{ backgroundColor: 'var(--background-card)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }} />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-primary)' }}>商品名 *</label>
              <input type="text" value={formData.productName} onChange={(e) => setFormData({ ...formData, productName: e.target.value })} required className="w-full px-4 py-2 border rounded-lg" style={{ backgroundColor: 'var(--background-card)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }} />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-primary)' }}>商品名（カナ）</label>
              <input type="text" value={formData.productNameKana} onChange={(e) => setFormData({ ...formData, productNameKana: e.target.value })} placeholder="ショウヒンメイ" className="w-full px-4 py-2 border rounded-lg" style={{ backgroundColor: 'var(--background-card)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }} />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-primary)' }}>商品説明</label>
              <textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} rows={4} placeholder="商品の詳細説明を入力..." className="w-full px-4 py-2 border rounded-lg resize-none" style={{ backgroundColor: 'var(--background-card)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }} />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-primary)' }}>原価</label>
                <input type="number" min="0" step="1" value={formData.costPrice} onChange={(e) => setFormData({ ...formData, costPrice: e.target.value })} className="w-full px-4 py-2 border rounded-lg" style={{ backgroundColor: 'var(--background-card)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }} />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-primary)' }}>販売価格 *</label>
                <input type="number" min="0" step="1" value={formData.sellingPrice} onChange={(e) => setFormData({ ...formData, sellingPrice: e.target.value })} required className="w-full px-4 py-2 border rounded-lg" style={{ backgroundColor: 'var(--background-card)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }} />
              </div>
            </div>

            {/* バリエーション */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>バリエーション</label>
                <button type="button" onClick={addVariant} className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1">
                  <Plus size={16} />追加
                </button>
              </div>
              {variants.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-4 border rounded-lg" style={{ borderColor: 'var(--border-color)' }}>バリエーションがありません</p>
              ) : (
                variants.map((v, i) => (
                  <div key={i} className="flex items-center gap-2 mb-2 p-3 rounded-lg" style={{ backgroundColor: 'var(--light-gray-primary)' }}>
                    <input type="text" placeholder="コード" value={v.variantCode} onChange={(e) => updateVariant(i, 'variantCode', e.target.value)} className="flex-1 px-3 py-1.5 border rounded text-sm" style={{ backgroundColor: 'var(--background-card)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }} />
                    <input type="text" placeholder="サイズ" value={v.size} onChange={(e) => updateVariant(i, 'size', e.target.value)} className="w-20 px-3 py-1.5 border rounded text-sm" style={{ backgroundColor: 'var(--background-card)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }} />
                    <input type="text" placeholder="色" value={v.color} onChange={(e) => updateVariant(i, 'color', e.target.value)} className="w-20 px-3 py-1.5 border rounded text-sm" style={{ backgroundColor: 'var(--background-card)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }} />
                    <input type="text" placeholder="JAN" value={v.janCode} onChange={(e) => updateVariant(i, 'janCode', e.target.value)} className="w-28 px-3 py-1.5 border rounded text-sm" style={{ backgroundColor: 'var(--background-card)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }} />
                    <input type="number" min="0" placeholder="在庫" value={v.initialStock} onChange={(e) => updateVariant(i, 'initialStock', parseInt(e.target.value) || 0)} className="w-20 px-3 py-1.5 border rounded text-sm" style={{ backgroundColor: 'var(--background-card)', borderColor: 'var(--border-color)', color: 'var(--text-primary)' }} />
                    <button type="button" onClick={() => removeVariant(i)} className="p-1 text-red-600 hover:bg-red-50 rounded"><X size={16} /></button>
                  </div>
                ))
              )}
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <button type="button" onClick={onClose} className="btn-secondary">キャンセル</button>
              <button type="submit" disabled={loading} className="btn-primary">{loading ? '登録中...' : '登録'}</button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

// 商品編集モーダル
function EditProductModal({ product, onClose, onSuccess }: { product: Product; onClose: () => void; onSuccess: () => void }) {
  const [formData, setFormData] = useState({
    skuCode: product.skuCode,
    janCode: product.janCode || '',
    productName: product.productName,
    productNameKana: product.productNameKana || '',
    description: product.description || '',
    costPrice: product.costPrice || '',
    sellingPrice: product.sellingPrice,
    status: product.status,
  });
  const [images, setImages] = useState<string[]>(product.imageUrls || []);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState<'basic' | 'variants' | 'mappings'>('basic');
  const [variants, setVariants] = useState<Variant[]>([]);
  const [shops, setShops] = useState<Shop[]>([]);
  const [mappings, setMappings] = useState<any[]>([]);

  useEffect(() => {
    fetchProductDetails();
    fetchShops();
  }, []);

  async function fetchProductDetails() {
    try {
      const response = await apiFetch(`/api/products/${product.id}`);
      const data = await response.json();
      if (response.ok && data.product) {
        setVariants(data.product.variants || []);
        setMappings(data.product.mappings || []);
        setImages(data.product.imageUrls || []);
      }
    } catch (err) {
      console.error('Fetch error:', err);
    }
  }

  async function fetchShops() {
    try {
      const response = await apiFetch('/api/shops');
      const data = await response.json();
      if (response.ok) {
        setShops(data.shops || []);
      }
    } catch (err) {
      console.error('Fetch shops error:', err);
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await apiFetch(`/api/products/${product.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...formData,
          janCode: formData.janCode || null,
          productNameKana: formData.productNameKana || null,
          description: formData.description || null,
          costPrice: formData.costPrice ? parseFloat(formData.costPrice) : null,
          sellingPrice: parseFloat(formData.sellingPrice),
          imageUrls: images.filter(img => !img.startsWith('data:')),
        }),
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.error);
      onSuccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'エラーが発生しました');
    } finally {
      setLoading(false);
    }
  }

  async function addVariant() {
    const variantCode = prompt('バリアントコードを入力:');
    if (!variantCode) return;

    try {
      const response = await apiFetch(`/api/products/${product.id}/variants`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ variantCode }),
      });
      if (response.ok) fetchProductDetails();
    } catch (err) {
      console.error('Add variant error:', err);
    }
  }

  async function deleteVariant(variantId: string) {
    if (!confirm('このバリエーションを削除しますか？')) return;
    try {
      await apiFetch(`/api/products/${product.id}/variants/${variantId}`, { method: 'DELETE' });
      fetchProductDetails();
    } catch (err) {
      console.error('Delete variant error:', err);
    }
  }

  async function addMapping() {
    if (shops.length === 0) {
      alert('先に店舗を登録してください');
      return;
    }
    const shopId = prompt('店舗を選択:\n' + shops.map((s, i) => `${i + 1}. ${s.shopName}`).join('\n'));
    if (!shopId) return;
    const selectedShop = shops[parseInt(shopId) - 1];
    if (!selectedShop) return;
    const mallItemCode = prompt('モール商品コードを入力:');
    if (!mallItemCode) return;

    try {
      await apiFetch(`/api/products/${product.id}/mappings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ shopId: selectedShop.id, mallItemCode }),
      });
      fetchProductDetails();
    } catch (err) {
      console.error('Add mapping error:', err);
    }
  }

  async function deleteMapping(mappingId: string) {
    if (!confirm('このモール連携を削除しますか？')) return;
    try {
      await apiFetch(`/api/products/${product.id}/mappings/${mappingId}`, { method: 'DELETE' });
      fetchProductDetails();
    } catch (err) {
      console.error('Delete mapping error:', err);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="w-full max-w-4xl max-h-[90vh] overflow-y-auto rounded-lg shadow-xl" style={{ backgroundColor: 'var(--background-card)' }}>
        <div className="p-6">
          <h2 className="text-xl font-bold mb-4" style={{ color: 'var(--text-primary)' }}>商品編集</h2>

          {error && <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">{error}</div>}

          {/* タブ */}
          <div className="flex gap-4 border-b mb-4" style={{ borderColor: 'var(--border-color)' }}>
            {[
              { id: 'basic', label: '基本情報', icon: Package },
              { id: 'variants', label: 'バリエーション', icon: Layers },
              { id: 'mappings', label: 'モール連携', icon: Store },
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`pb-2 px-1 flex items-center gap-1 border-b-2 transition-colors ${
                  activeTab === tab.id ? 'border-blue-600 text-blue-600' : 'border-transparent'
                }`}
              >
                <tab.icon size={16} />
                {tab.label}
              </button>
            ))}
          </div>

          {activeTab === 'basic' && (
            <form onSubmit={handleSubmit} className="space-y-4">
              <ImageUploader images={images} onChange={setImages} productId={product.id} />

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">SKUコード</label>
                  <input type="text" value={formData.skuCode} onChange={(e) => setFormData({ ...formData, skuCode: e.target.value })} required className="w-full px-3 py-2 border rounded-lg" style={{ backgroundColor: 'var(--background-card)', borderColor: 'var(--border-color)' }} />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">JANコード</label>
                  <input type="text" value={formData.janCode} onChange={(e) => setFormData({ ...formData, janCode: e.target.value })} className="w-full px-3 py-2 border rounded-lg" style={{ backgroundColor: 'var(--background-card)', borderColor: 'var(--border-color)' }} />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">商品名</label>
                <input type="text" value={formData.productName} onChange={(e) => setFormData({ ...formData, productName: e.target.value })} required className="w-full px-3 py-2 border rounded-lg" style={{ backgroundColor: 'var(--background-card)', borderColor: 'var(--border-color)' }} />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">商品名（カナ）</label>
                <input type="text" value={formData.productNameKana} onChange={(e) => setFormData({ ...formData, productNameKana: e.target.value })} className="w-full px-3 py-2 border rounded-lg" style={{ backgroundColor: 'var(--background-card)', borderColor: 'var(--border-color)' }} />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">商品説明</label>
                <textarea value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} rows={4} className="w-full px-3 py-2 border rounded-lg resize-none" style={{ backgroundColor: 'var(--background-card)', borderColor: 'var(--border-color)' }} />
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">販売価格</label>
                  <input type="number" min="0" value={formData.sellingPrice} onChange={(e) => setFormData({ ...formData, sellingPrice: e.target.value })} required className="w-full px-3 py-2 border rounded-lg" style={{ backgroundColor: 'var(--background-card)', borderColor: 'var(--border-color)' }} />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">原価</label>
                  <input type="number" min="0" value={formData.costPrice} onChange={(e) => setFormData({ ...formData, costPrice: e.target.value })} className="w-full px-3 py-2 border rounded-lg" style={{ backgroundColor: 'var(--background-card)', borderColor: 'var(--border-color)' }} />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">ステータス</label>
                  <select value={formData.status} onChange={(e) => setFormData({ ...formData, status: e.target.value })} className="w-full px-3 py-2 border rounded-lg" style={{ backgroundColor: 'var(--background-card)', borderColor: 'var(--border-color)' }}>
                    <option value="active">有効</option>
                    <option value="inactive">無効</option>
                    <option value="discontinued">廃盤</option>
                  </select>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button type="button" onClick={onClose} className="btn-secondary">キャンセル</button>
                <button type="submit" disabled={loading} className="btn-primary">{loading ? '更新中...' : '更新'}</button>
              </div>
            </form>
          )}

          {activeTab === 'variants' && (
            <div className="space-y-4">
              <div className="flex justify-end">
                <button onClick={addVariant} className="btn-primary flex items-center gap-1">
                  <Plus size={16} />バリエーション追加
                </button>
              </div>

              {variants.length === 0 ? (
                <p className="text-center py-8 text-gray-500">バリエーションがありません</p>
              ) : (
                <table className="w-full">
                  <thead>
                    <tr style={{ backgroundColor: 'var(--light-gray-primary)' }}>
                      <th className="px-3 py-2 text-left text-xs">コード</th>
                      <th className="px-3 py-2 text-left text-xs">サイズ</th>
                      <th className="px-3 py-2 text-left text-xs">色</th>
                      <th className="px-3 py-2 text-left text-xs">JAN</th>
                      <th className="px-3 py-2 text-right text-xs">在庫</th>
                      <th className="px-3 py-2 text-center text-xs">操作</th>
                    </tr>
                  </thead>
                  <tbody>
                    {variants.filter(v => v.status !== 'inactive').map((v) => (
                      <tr key={v.id} className="border-b" style={{ borderColor: 'var(--border-color)' }}>
                        <td className="px-3 py-2 text-sm font-mono">{v.variantCode}</td>
                        <td className="px-3 py-2 text-sm">{v.size || '-'}</td>
                        <td className="px-3 py-2 text-sm">{v.color || '-'}</td>
                        <td className="px-3 py-2 text-sm">{v.janCode || '-'}</td>
                        <td className="px-3 py-2 text-sm text-right font-medium">{v.inventory?.freeStock ?? '-'}</td>
                        <td className="px-3 py-2 text-center">
                          <button onClick={() => deleteVariant(v.id)} className="p-1 text-red-600 hover:bg-red-50 rounded">
                            <Trash2 size={16} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              <div className="flex justify-end pt-4">
                <button onClick={onClose} className="btn-secondary">閉じる</button>
              </div>
            </div>
          )}

          {activeTab === 'mappings' && (
            <div className="space-y-4">
              <div className="flex justify-end">
                <button onClick={addMapping} className="btn-primary flex items-center gap-1">
                  <Link size={16} />モール連携追加
                </button>
              </div>

              {mappings.length === 0 ? (
                <p className="text-center py-8 text-gray-500">モール連携がありません</p>
              ) : (
                <div className="space-y-2">
                  {mappings.map((m) => (
                    <div key={m.id} className="flex items-center justify-between p-3 rounded-lg" style={{ backgroundColor: 'var(--light-gray-primary)' }}>
                      <div>
                        <span className="font-medium">{m.shop?.shopName || '不明'}</span>
                        <span className="ml-2 text-sm font-mono text-gray-500">{m.mallItemCode}</span>
                      </div>
                      <button onClick={() => deleteMapping(m.id)} className="p-1 text-red-600 hover:bg-red-50 rounded">
                        <Trash2 size={16} />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <div className="flex justify-end pt-4">
                <button onClick={onClose} className="btn-secondary">閉じる</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// 商品詳細モーダル
function ProductDetailModal({ productId, onClose, onEdit }: { productId: string; onClose: () => void; onEdit: (product: Product) => void }) {
  const [product, setProduct] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    fetchProduct();
  }, [productId]);

  async function fetchProduct() {
    try {
      const response = await apiFetch(`/api/products/${productId}`);
      const data = await response.json();
      if (response.ok) setProduct(data.product);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="w-full max-w-4xl max-h-[90vh] overflow-y-auto rounded-lg shadow-xl" style={{ backgroundColor: 'var(--background-card)' }}>
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold" style={{ color: 'var(--text-primary)' }}>商品詳細</h2>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg"><X size={20} /></button>
          </div>

          {loading ? (
            <div className="text-center py-8">読み込み中...</div>
          ) : product ? (
            <div className="space-y-6">
              {/* 画像ギャラリー */}
              {product.imageUrls && product.imageUrls.length > 0 && (
                <div>
                  <div className="aspect-video bg-gray-100 rounded-lg overflow-hidden mb-2">
                    <img src={product.imageUrls[currentImageIndex]} alt={product.productName} className="w-full h-full object-contain" />
                  </div>
                  {product.imageUrls.length > 1 && (
                    <div className="flex gap-2 overflow-x-auto">
                      {product.imageUrls.map((url: string, i: number) => (
                        <button
                          key={i}
                          onClick={() => setCurrentImageIndex(i)}
                          className={`w-16 h-16 rounded-lg overflow-hidden flex-shrink-0 border-2 ${i === currentImageIndex ? 'border-blue-600' : 'border-transparent'}`}
                        >
                          <img src={url} alt="" className="w-full h-full object-cover" />
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* 基本情報 */}
              <div className="grid grid-cols-2 gap-4 p-4 rounded-lg" style={{ backgroundColor: 'var(--light-gray-primary)' }}>
                <div><span className="text-sm text-gray-500">SKU:</span> <strong>{product.skuCode}</strong></div>
                <div><span className="text-sm text-gray-500">JAN:</span> <strong>{product.janCode || '-'}</strong></div>
                <div className="col-span-2"><span className="text-sm text-gray-500">商品名:</span> <strong>{product.productName}</strong></div>
                <div><span className="text-sm text-gray-500">販売価格:</span> <strong>¥{Number(product.sellingPrice).toLocaleString()}</strong></div>
                <div><span className="text-sm text-gray-500">原価:</span> <strong>¥{Number(product.costPrice || 0).toLocaleString()}</strong></div>
              </div>

              {product.description && (
                <div className="p-4 rounded-lg" style={{ backgroundColor: 'var(--light-gray-primary)' }}>
                  <span className="text-sm text-gray-500">商品説明:</span>
                  <p className="mt-1 whitespace-pre-wrap">{product.description}</p>
                </div>
              )}

              {/* バリエーション */}
              <div>
                <h3 className="font-semibold mb-2">バリエーション ({product.variants?.length || 0})</h3>
                {product.variants?.length > 0 ? (
                  <table className="w-full">
                    <thead>
                      <tr style={{ backgroundColor: 'var(--light-gray-primary)' }}>
                        <th className="px-3 py-2 text-left text-xs">コード</th>
                        <th className="px-3 py-2 text-left text-xs">サイズ</th>
                        <th className="px-3 py-2 text-left text-xs">色</th>
                        <th className="px-3 py-2 text-right text-xs">在庫</th>
                      </tr>
                    </thead>
                    <tbody>
                      {product.variants.filter((v: any) => v.status !== 'inactive').map((v: any) => (
                        <tr key={v.id} className="border-b" style={{ borderColor: 'var(--border-color)' }}>
                          <td className="px-3 py-2 text-sm font-mono">{v.variantCode}</td>
                          <td className="px-3 py-2 text-sm">{v.size || '-'}</td>
                          <td className="px-3 py-2 text-sm">{v.color || '-'}</td>
                          <td className="px-3 py-2 text-sm text-right font-medium">{v.inventory?.freeStock ?? '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <p className="text-sm text-gray-500">バリエーションがありません</p>
                )}
              </div>
            </div>
          ) : (
            <div className="text-center py-8">商品を取得できませんでした</div>
          )}

          <div className="flex justify-end gap-3 mt-6">
            {product && (
              <button onClick={() => onEdit(product)} className="btn-primary flex items-center gap-1">
                <Edit2 size={16} />編集
              </button>
            )}
            <button onClick={onClose} className="btn-secondary">閉じる</button>
          </div>
        </div>
      </div>
    </div>
  );
}
